'use client';
import React, { useState } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from 'recharts';

interface TestSession {
  id: string;
  date: string;
  eps: number;
  engineScore: number;
  ignitionScore: number;
  bounceScore: number;
}

interface TrendChartSectionProps {
  sessions: TestSession[];
}

const LINES = [
  { key: 'EPS', color: '#FF3B30', strokeWidth: 2.5, dotRadius: 5 },
  { key: 'Engine', color: '#8B0000', strokeWidth: 1.5, dotRadius: 3 },
  { key: 'Ignition', color: '#F59E0B', strokeWidth: 1.5, dotRadius: 3 },
  { key: 'Bounce', color: '#B3B3B3', strokeWidth: 1.5, dotRadius: 3 },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-xl p-3.5 shadow-xl min-w-[180px]"
      style={{ background: 'rgba(20,20,20,0.95)', border: '1px solid rgba(225,6,0,0.2)' }}
    >
      <p className="text-xs text-zinc-500 mb-2.5 font-medium">{label}</p>
      {payload.map((p: any) => (
        <div key={`tt-${p.dataKey}`} className="flex items-center justify-between gap-6 text-sm mb-1">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-zinc-400">{p.name}</span>
          </div>
          <span className="font-mono tabular-nums font-bold" style={{ color: p.color }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function TrendChartSection({ sessions }: TrendChartSectionProps) {
  const [visibleLines, setVisibleLines] = useState<Record<string, boolean>>({
    EPS: true, Engine: true, Ignition: true, Bounce: true,
  });

  const data = sessions.map(s => ({
    date: new Date(s.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    EPS: s.eps,
    Engine: s.engineScore,
    Ignition: s.ignitionScore,
    Bounce: s.bounceScore,
  }));

  const toggleLine = (key: string) => {
    setVisibleLines(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="card p-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="section-header">Score Progression</h3>
          <p className="text-xs text-zinc-500 mt-0.5">All {sessions.length} test sessions</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {LINES.map(l => (
            <button
              key={`toggle-${l.key}`}
              onClick={() => toggleLine(l.key)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all duration-150 border ${
                visibleLines[l.key] ? 'border-transparent' : 'border-zinc-700 bg-zinc-800 text-zinc-500 opacity-50'
              }`}
              style={visibleLines[l.key] ? { backgroundColor: `${l.color}15`, color: l.color, border: `1px solid ${l.color}30` } : {}}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: visibleLines[l.key] ? l.color : '#52525b' }} />
              {l.key}
            </button>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#52525b' }} axisLine={false} tickLine={false} />
          <YAxis domain={[40, 100]} tick={{ fontSize: 11, fill: '#52525b', fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={80} stroke="#E10600" strokeDasharray="4 4" strokeOpacity={0.2} />
          <ReferenceLine y={60} stroke="#8B0000" strokeDasharray="4 4" strokeOpacity={0.2} />
          {LINES.map(l => (
            visibleLines[l.key] && (
              <Line
                key={`line-${l.key}`}
                type="monotone"
                dataKey={l.key}
                stroke={l.color}
                strokeWidth={l.strokeWidth}
                dot={{ fill: l.color, r: l.dotRadius, strokeWidth: 0 }}
                activeDot={{ r: l.dotRadius + 2, strokeWidth: 2, stroke: l.color, fill: 'hsl(240 8% 7%)' }}
              />
            )
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}